/**
 * Maps QA route slugs to agent IDs per environment.
 * If a slug is not found here, it is used directly as the agent ID.
 */

type Environment = "prod" | "stage" | "dev";

export interface QaAgentCapabilities {
  selectionContext: boolean;
  clientTools: boolean;
  showAgentDropdown: boolean;
}

interface QaAgentConfig {
  agentIds: Partial<Record<Environment, string>>;
  capabilities?: Partial<QaAgentCapabilities>;
}

const defaultCapabilities: QaAgentCapabilities = {
  selectionContext: true,
  clientTools: true,
  showAgentDropdown: true,
};

const qaAgentMap: Record<string, QaAgentConfig> = {
  // example:
  // "field-notes": { agentIds: { prod: "prod-agent-id", stage: "stage-agent-id", dev: "dev-agent-id" } },
  quadriassistant: {
    agentIds: {
      dev: "2425afb1-3988-4845-b85e-155e5d747df2",
      stage: "1f01c54f-252c-4c98-89f1-11006bd2e0af",
      prod: "78e96c6e-c0d7-47e4-a03e-448dbc3d8244",
    },
  },
  "novapoint-assistant": {
    agentIds: {
      dev: "2425afb1-3988-4845-b85e-155e5d747df2",
      stage: "1f01c54f-252c-4c98-89f1-11006bd2e0af",
      prod: "78e96c6e-c0d7-47e4-a03e-448dbc3d8244",
    },
  },
  "tcom-assistant": {
    agentIds: {
      dev: "529027b3-ff90-4669-ba26-fc8b6f8f4518",
      stage: "529027b3-ff90-4669-ba26-fc8b6f8f4518",
      prod: "c9a66b77-c665-4169-b519-3e7c4cec1a8d",
    },
    capabilities: {
      selectionContext: false,
      clientTools: false,
      showAgentDropdown: false,
    },
  },
};

export function resolveQaAgentId(slug: string, env: Environment): string {
  const entry = qaAgentMap[slug];
  return entry?.agentIds[env] ?? slug;
}

export function resolveQaProductAgentIds(env: Environment): string[] {
  return [
    ...new Set(
      Object.values(qaAgentMap).flatMap(({ agentIds }) =>
        agentIds[env] ? [agentIds[env]] : [],
      ),
    ),
  ];
}

export function resolveQaAgentCapabilities(
  slug: string | undefined,
): QaAgentCapabilities {
  return {
    ...defaultCapabilities,
    ...(slug ? qaAgentMap[slug]?.capabilities : undefined),
  };
}
